/**
 * Video feature — React Query hooks
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getValidAccessTokenFromStorage } from "@weco/suite-client";
import { videoService } from "../api";
import type { CreateRoomRequest } from "../api";
import { isNotFoundError } from "../lib/meetingIdentifier";
import { suiteQueryRetry } from "../../../lib/throttleRetry";

export function useRooms() {
  return useQuery({
    queryKey: ["video", "rooms"],
    queryFn: () => videoService.getRooms(),
    refetchInterval: 10000, // Refresh every 10 seconds for active status
  });
}

export function useRoom(
  roomId: string | null,
  options?: { pollIntervalMs?: number | false },
) {
  // RoomRoute disables polling during the in-call phase. Without that, every
  // refetch produces a fresh `room.livekit` reference, which propagates as a
  // new `token` prop on <LiveKitRoom> and triggers a disconnect/reconnect —
  // the disconnect fires `onDisconnected` and flips RoomRoute into the
  // "Call ended" phase right after the user joins.
  const interval = options?.pollIntervalMs;
  return useQuery({
    queryKey: ["video", "room", roomId],
    queryFn: async () => {
      // Anonymous visitors need skipAuth so the suite-client doesn't throw
      // a synthetic 401 before reaching the backend's allow_guests path.
      const token = await getValidAccessTokenFromStorage();
      return videoService.getRoom(roomId!, { skipAuth: !token });
    },
    enabled: !!roomId,
    // A 404 is final (GH-1855): no retry, and stop polling a meeting that
    // does not exist.
    retry: (failureCount, error) =>
      !isNotFoundError(error) && suiteQueryRetry(failureCount, error),
    refetchInterval: (query) => {
      if (isNotFoundError(query.state.error)) return false;
      return interval === false ? false : interval ?? 5000;
    },
  });
}

export function useCreateRoom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateRoomRequest) => videoService.createRoom(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["video", "rooms"] });
    },
  });
}

export function useDeleteRoom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (roomId: string) => videoService.deleteRoom(roomId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["video", "rooms"] });
    },
  });
}

